/**
 * Resolve the active sequence for live training UI.
 *
 * Prefer the live 0-based index from socket state (`currentSequenceIndexByTeId`)
 * over `exercise.currentSequence`, which is often wiped by API refresh and not
 * always re-patched on timer ticks.
 *
 * @param {object|null|undefined} exercise
 * @param {number|null|undefined} liveIndex0 0-based index from socket map
 * @returns {{ seqs: object[], idx: number, number: number, obj: object|null }}
 */
export function resolveLiveSequence(exercise, liveIndex0 = null) {
  const seqs = [...(exercise?.sequences || [])].sort(
    (a, b) => (a.sequence_order ?? 0) - (b.sequence_order ?? 0)
  );

  let idx = 0;
  const hasLive =
    liveIndex0 != null &&
    liveIndex0 !== "" &&
    Number.isFinite(Number(liveIndex0));

  if (hasLive && seqs.length > 0) {
    idx = Math.max(
      0,
      Math.min(seqs.length - 1, Math.floor(Number(liveIndex0)))
    );
  } else {
    const cs = exercise?.currentSequence;
    if (cs && typeof cs === "object") {
      const byTes = seqs.findIndex(
        (s) => cs.tes_id != null && String(s.tes_id) === String(cs.tes_id)
      );
      const byOrder = seqs.findIndex(
        (s) =>
          cs.sequence_order != null && s.sequence_order === cs.sequence_order
      );
      idx = byTes >= 0 ? byTes : byOrder >= 0 ? byOrder : 0;
    } else if (typeof cs === "number") {
      const byOrder = seqs.findIndex((s) => s.sequence_order === cs);
      idx =
        byOrder >= 0
          ? byOrder
          : Math.max(0, Math.min(Math.max(seqs.length - 1, 0), cs));
    }
  }

  const obj = seqs[idx] || null;
  const number = obj?.sequence_order ?? (seqs.length ? idx + 1 : 1);
  return { seqs, idx, number, obj };
}

/**
 * Build a display-friendly currentSequence value from a live 0-based index.
 * Prefers the matching sequence row's order when sequences are present.
 */
export function currentSequenceFromLiveIndex(exercise, liveIndex0) {
  if (liveIndex0 == null || !Number.isFinite(Number(liveIndex0))) {
    return undefined;
  }
  const { number, obj } = resolveLiveSequence(exercise, liveIndex0);
  if (obj) return obj;
  return number;
}
